import styled from "@emotion/styled";
import { Box, Grid, IconButton, Stack, type Theme, Typography, useTheme } from "@mui/material";
import { Button as MuiButton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as ArrowRightIcon } from "Components/_Shared/Icons/Assets/arrow-right.svg";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { ReactComponent as InfoIcon } from "Components/_Shared/Icons/Assets/info.svg";
import { ReactComponent as SubtractIcon } from "Components/_Shared/Icons/Assets/subtract.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { getListInsuranceRisksQueryKey, useDeleteInsuranceRisk, useUpdateInsuranceRisk } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type Risk as RiskDto, type UpdateRiskBody } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { RISK_TYPE_RESOURCE_KEY } from "Infrastructure/Utils/InsuranceUtils";
import { type FC, useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { RecommendedAmountsTooltip } from "./RecommendedAmountsTooltip";
import { type RiskFormValues, type SerialQueue, buildUpdateRiskBody, createSerialQueue } from "./Risk.helpers";
import { ThumbsUpIcon } from "./ThumbsUpIcon";

type Props = {
  readonly analysisId: string;
  readonly versionId: string;
  readonly clientId: string;
  readonly risk: RiskDto;
};

type Form = RiskFormValues;

const StyledStack = styled(Stack)`
  background: ${(props) => props.theme.colors.backgroundSecondary};
  padding: ${(props) => props.theme.spacing(Spacing.XL)};
  border-radius: ${(props) => props.theme.spacing(Radius.XL)};
`;

const getThumbsUpColor = (currentAmount: number, recommendedAmount: number, theme: Theme) => {
  if (currentAmount === 0) {
    if (recommendedAmount === 0) return theme.palette.success.main;
    return recommendedAmount > 0 ? theme.palette.error.main : "#946800";
  }

  const diff = recommendedAmount - currentAmount;
  const percentDiff = diff / Math.abs(currentAmount);

  if (Math.abs(percentDiff) < 0.1) {
    return theme.palette.success.main;
  }
  if (-percentDiff > 0.1) {
    return "#946800";
  }
  return theme.palette.error.main;
};

export const Risk: FC<Props> = ({ risk, analysisId, versionId, clientId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();

  const [parametersExpanded, setParametersExpanded] = useState(false);

  const { mutateAsync: deleteInsuranceRisk } = useDeleteInsuranceRisk({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInsuranceRisksQueryKey(analysisId, versionId, clientId) });
        toast.success("Riziko úspěšně smazáno.");
      },
    },
  });

  const { mutateAsync: updateInsuranceRisk } = useUpdateInsuranceRisk({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListInsuranceRisksQueryKey(analysisId, versionId, clientId) });
        toast.success("Riziko úspěšně upraveno.");
      },
    },
  });

  const defaultValues: Form = {
    currentAmount: risk?.currentAmount.value ?? ("0" as any),
    currentAgeTo: risk?.currentAgeTo ?? ("0" as any),
    recommendedAmount: risk?.recommendedAmount.value ?? ("0" as any),
    recommendedAgeTo: risk?.recommendedAgeTo ?? ("0" as any),
    requiredBaseAmount: risk?.requiredBaseAmount.value.value ?? ("0" as any),
    infinityRent: risk?.infinityRent.value.value ?? ("0" as any),
    requiredCoverageCompensation: risk?.requiredCoverageCompensation.value.value ?? ("0" as any),
  };

  const {
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  const currentAmount = watch("currentAmount");
  const currentAgeTo = watch("currentAgeTo");
  const recommendedAmount = watch("recommendedAmount");
  const recommendedAgeTo = watch("recommendedAgeTo");
  const requiredBaseAmount = watch("requiredBaseAmount");
  const infinityRent = watch("infinityRent");
  const requiredCoverageCompensation = watch("requiredCoverageCompensation");

  // One queue per mounted risk (the list keys by guid), so a slower older update cannot overwrite
  // newer field values.
  const updateQueueRef = useRef<SerialQueue | null>(null);
  if (updateQueueRef.current === null) {
    updateQueueRef.current = createSerialQueue();
  }
  const enqueueUpdate = updateQueueRef.current;

  const onUpdate = async (updates: Partial<UpdateRiskBody>) => {
    // Snapshotted at blur time, so the queue applies the snapshots in the order of the edits.
    const data = buildUpdateRiskBody(
      risk,
      {
        currentAmount,
        currentAgeTo,
        recommendedAmount,
        recommendedAgeTo,
        requiredBaseAmount,
        infinityRent,
        requiredCoverageCompensation,
      },
      updates,
    );

    try {
      await enqueueUpdate(() =>
        updateInsuranceRisk({
          analysisId,
          versionId,
          clientGuid: clientId,
          riskGuid: risk.guid,
          data,
        }),
      );
    } catch {
      toast.error(t(Resources.Common.Error));
    }
  };

  // biome-ignore lint: <Open is required.>
  useEffect(() => {
    reset(defaultValues);
  }, [risk, reset]);

  return (
    <StyledStack spacing={Spacing.L}>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="body1" fontWeight={600}>
          {t(Resources.AnalysisDetail.Insurance.InsuranceRiskType[RISK_TYPE_RESOURCE_KEY[risk.riskType]])}
        </Typography>
        <IconButton
          sx={{ padding: theme.spacing(Spacing.S) }}
          onClick={async () => {
            try {
              await deleteInsuranceRisk({ analysisId, versionId, clientGuid: clientId, riskGuid: risk.guid });
            } catch {
              toast.error(t(Resources.Common.Error));
            }
          }}
        >
          <BinIcon width={18} height={18} />
        </IconButton>
      </Box>
      <Grid container>
        <Grid size={5.25}>
          <Typography fontWeight={500} variant="body2" mb={Spacing.M}>
            {t(Resources.AnalysisDetail.Insurance.CurrentState)}
          </Typography>
          <Grid container gap={Spacing.M} display="flex">
            <Grid size={8.7}>
              <BlFormInput
                name="currentAmount"
                control={control}
                errors={errors}
                inputEndAdornment={
                  <Typography variant="body2" color="secondary">
                    {t(Resources.Common.CZKSuffix)}
                  </Typography>
                }
                unmask="typed"
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                  },
                ]}
                onBlur={async () => {
                  await onUpdate({
                    currentAmount: {
                      value: currentAmount,
                      currencyCode: "CZK",
                      frequency: AmountFrequency.one_time,
                    },
                  });
                }}
              />
            </Grid>
            <Grid size={3}>
              <BlFormInput
                name="currentAgeTo"
                control={control}
                errors={errors}
                inputEndAdornment={
                  <Typography variant="body2" color="secondary">
                    {t(Resources.Common.Years)}
                  </Typography>
                }
                unmask="typed"
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                  },
                ]}
                onBlur={async () => {
                  await onUpdate({
                    currentAgeTo,
                  });
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={1.5}>
          <Box display="flex" justifyContent="center" alignItems="flex-end" height="100%">
            <Box width={60} display="flex" justifyContent="space-between" alignItems="center" mb={Spacing.S}>
              <ThumbsUpIcon
                error={getThumbsUpColor(risk.currentAmount.value, risk.recommendedAmount.value, theme) === theme.palette.error.main}
                backgroundColor={getThumbsUpColor(risk.currentAmount.value, risk.recommendedAmount.value, theme)}
              />
              <ArrowRightIcon width={18} height={18} fill={theme.colors.textSecondary} />
            </Box>
          </Box>
        </Grid>
        <Grid size={5.25}>
          <Box display="flex" alignItems="center" mb={Spacing.S}>
            <Typography mr={Spacing.S} fontWeight={500} variant="body2">
              {t(Resources.AnalysisDetail.Insurance.RecommendedState)}
            </Typography>
            {risk.recommendedAmountLabels && risk.recommendedAmountLabels.length > 0 && (
              <RecommendedAmountsTooltip labels={risk.recommendedAmountLabels} />
            )}
          </Box>
          <Grid container gap={Spacing.M} display="flex">
            <Grid size={8.7}>
              <BlFormInput
                name="recommendedAmount"
                control={control}
                errors={errors}
                disabled={readOnly}
                inputEndAdornment={
                  <Typography variant="body2" color="secondary">
                    {t(Resources.Common.CZKSuffix)}
                  </Typography>
                }
                unmask="typed"
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                  },
                ]}
                onBlur={async () => {
                  await onUpdate({
                    recommendedAmount: {
                      value: recommendedAmount,
                      currencyCode: "CZK",
                      frequency: AmountFrequency.one_time,
                    },
                  });
                }}
              />
            </Grid>
            <Grid size={3}>
              <BlFormInput
                name="recommendedAgeTo"
                control={control}
                errors={errors}
                disabled={readOnly}
                inputEndAdornment={
                  <Typography variant="body2" color="secondary">
                    {t(Resources.Common.Years)}
                  </Typography>
                }
                unmask="typed"
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                  },
                ]}
                onBlur={async () => {
                  await onUpdate({
                    recommendedAgeTo,
                  });
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          {!parametersExpanded && risk.requiredBaseAmount.isVisible && (
            <Box display="inline-block" mr={Spacing.L}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "22px",
                  background: "rgba(0,0,0, 0.05)",
                  padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                  borderRadius: theme.spacing(Radius.XL),
                  marginBottom: theme.spacing(Spacing.M),
                }}
              >
                <Typography display="inline-block" variant="caption" fontWeight={500} lineHeight={1} mr={Spacing.M}>
                  {t(Resources.AnalysisDetail.Insurance.RequiredMinimum)}:{" "}
                  {formatCurrency(risk.requiredBaseAmount.value.value, risk.requiredBaseAmount.value.currencyCode)}
                </Typography>
              </Box>
            </Box>
          )}
          {!parametersExpanded && risk.infinityRent.isVisible && (
            <Box display="inline-block">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "22px",
                  background: "rgba(0,0,0, 0.05)",
                  padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                  borderRadius: theme.spacing(Radius.XL),
                }}
              >
                <Typography display="inline-block" variant="caption" fontWeight={500} lineHeight={1}>
                  {t(Resources.AnalysisDetail.Insurance.InfinityRent)}:{" "}
                  {formatCurrency(risk.infinityRent.value.value, risk.infinityRent.value.currencyCode)}
                </Typography>
              </Box>
            </Box>
          )}
          {!parametersExpanded && risk.infinityRent.isVisible && (
            <Box display="inline-block">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "22px",
                  background: "rgba(0,0,0, 0.05)",
                  padding: `${theme.spacing(Spacing.XS)} ${theme.spacing(Spacing.M)}`,
                  borderRadius: theme.spacing(Radius.XL),
                }}
              >
                <Typography display="inline-block" variant="caption" fontWeight={500} lineHeight={1}>
                  {t(Resources.AnalysisDetail.Insurance.Compensation)}:{" "}
                  {formatCurrency(risk.requiredCoverageCompensation.value.value, risk.requiredCoverageCompensation.value.currencyCode)}/
                  {t(Resources.Common.Month)}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {risk.requiredBaseAmount.isVisible || risk.infinityRent.isVisible || risk.requiredCoverageCompensation.isVisible ? (
          <MuiButton sx={{ minWidth: "fit-content" }} onClick={() => setParametersExpanded(!parametersExpanded)}>
            {parametersExpanded ? (
              <>
                <Box display="flex" alignItems="center" mr={Spacing.S}>
                  <SubtractIcon color={theme.colors.textSecondary} />
                </Box>
                <Typography variant="body2" color="secondary" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Insurance.HideCompensationParameters)}
                </Typography>
              </>
            ) : (
              <>
                <Box display="flex" alignItems="center" mr={Spacing.S}>
                  <AddIcon color={theme.colors.textSecondary} />
                </Box>
                <Typography variant="body2" color="secondary" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Insurance.ShowCompensationParameters)}
                </Typography>
              </>
            )}
          </MuiButton>
        ) : undefined}
      </Box>
      {parametersExpanded &&
      (risk.requiredBaseAmount.isVisible || risk.infinityRent.isVisible || risk.requiredCoverageCompensation.isVisible) ? (
        <Box sx={{ background: theme.colors.backgroundTertiary, padding: Spacing.XL, borderRadius: Radius.L }}>
          <Grid container spacing={Spacing.L}>
            {risk.requiredBaseAmount.isVisible && (
              <Grid size={4}>
                <BlFormInput
                  name="requiredBaseAmount"
                  control={control}
                  label={t(Resources.AnalysisDetail.Insurance.InsuranceAmountRequiredMinimum)}
                  errors={errors}
                  disabled={readOnly}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                  onBlur={async () => {
                    if (requiredBaseAmount === risk.requiredBaseAmount.value.value) return;
                    await onUpdate({
                      requiredBaseAmount: {
                        value: {
                          value: requiredBaseAmount,
                          currencyCode: "CZK",
                          frequency: AmountFrequency.one_time,
                        },
                        hasChanged: true,
                      },
                    });
                  }}
                />
              </Grid>
            )}
            {risk.infinityRent.isVisible && (
              <Grid size={4}>
                <BlFormInput
                  name="infinityRent"
                  control={control}
                  label={t(Resources.AnalysisDetail.Insurance.InsuranceAmountTotalInfinitePension)}
                  errors={errors}
                  disabled={readOnly}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                  onBlur={async () => {
                    if (infinityRent === risk.infinityRent.value.value) return;
                    await onUpdate({
                      infinityRent: {
                        value: {
                          value: infinityRent,
                          currencyCode: "CZK",
                          frequency: AmountFrequency.one_time,
                        },
                        hasChanged: true,
                      },
                    });
                  }}
                />
              </Grid>
            )}
            {risk.requiredCoverageCompensation.isVisible && (
              <Grid size={4}>
                <BlFormInput
                  name="requiredCoverageCompensation"
                  control={control}
                  disabled={readOnly}
                  label={t(Resources.AnalysisDetail.Insurance.RequiredRiskCompensation)}
                  errors={errors}
                  inputEndAdornment={
                    <Typography variant="body2" color="secondary">
                      {t(Resources.Common.CZKSuffix)}/{t(Resources.Common.Month)}
                    </Typography>
                  }
                  unmask="typed"
                  mask={[
                    {
                      mask: Number,
                      thousandsSeparator: " ",
                      scale: 2,
                    },
                  ]}
                  onBlur={async () => {
                    if (requiredCoverageCompensation === risk.requiredCoverageCompensation.value.value) return;
                    await onUpdate({
                      requiredCoverageCompensation: {
                        value: {
                          value: requiredCoverageCompensation,
                          currencyCode: "CZK",
                          frequency: AmountFrequency.one_time,
                        },
                        hasChanged: true,
                      },
                    });
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      ) : undefined}
    </StyledStack>
  );
};
